import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button as MuiButton,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DatePicker, { registerLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { baseUrl } from "../config";
import SearchTextField from "./SearchTextField";

registerLocale("fr", fr);

function parseFilterDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function formatFilterDate(date) {
  if (!date || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${h}:${min}`;
}

const FILTER_TYPES = [
  { value: "date_start", label: "Début" },
  { value: "date_end", label: "Fin" },
  { value: "category", label: "Catégories" },
  { value: "name", label: "Nom" },
  { value: "author", label: "Auteur" },
  { value: "wiki_page", label: "Page Wikifield" },
];

const OPERATORS_BY_TYPE = {
  date_start: [
    { value: "after", label: "Après" },
    { value: "before", label: "Avant" },
    { value: "on", label: "Le" },
  ],
  date_end: [
    { value: "after", label: "Après" },
    { value: "before", label: "Avant" },
    { value: "on", label: "Le" },
  ],
  category: [
    { value: "contains", label: "Contient" },
    { value: "not_contains", label: "Ne contient pas" },
    { value: "empty", label: "Est vide" },
  ],
  name: [
    { value: "contains", label: "Contient" },
    { value: "not_contains", label: "Ne contient pas" },
  ],
  author: [
    { value: "is", label: "Est" },
    { value: "is_not", label: "N'est pas" },
  ],
  wiki_page: [{ value: "is", label: "Est" }],
};

const DEFAULT_RULE = {
  type: "category",
  op: "contains",
  values: [],
};

function makeDefaultFilters() {
  return {
    operator: "AND",
    conditions: [],
  };
}

function CategoryValueSelect({ values, onChange, categories }) {
  const [search, setSearch] = useState("");
  const selected = values || [];

  const filtered = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) &&
      !selected.includes(String(c.id))
  );

  const handleAdd = (id) => {
    onChange([...selected, String(id)]);
  };

  const handleRemove = (id) => {
    onChange(selected.filter((v) => v !== String(id)));
  };

  const selectedCats = selected
    .map((id) => categories.find((c) => String(c.id) === id))
    .filter(Boolean);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: 350, flexShrink: 0 }}>
      <SearchTextField
        placeholder="Chercher une catégorie…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {search && filtered.length > 0 && (
        <DropdownList>
          {filtered.slice(0, 10).map((cat) => (
            <DropdownItem key={cat.id} onClick={() => { handleAdd(cat.id); setSearch(""); }}>
              {cat.name}
            </DropdownItem>
          ))}
        </DropdownList>
      )}

      {/* Chips below the search box so they don't push the input downward */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minHeight: 0, maxHeight: 80, overflowY: "auto" }}>
        {selectedCats.map((cat) => (
          <Chip
            key={cat.id}
            label={cat.name}
            size="small"
            variant="filled"
            sx={{
              backgroundColor: "var(--accent)",
              color: "#13110e",
              "& .MuiChip-deleteIcon": { color: "#13110e" },
            }}
            onDelete={() => handleRemove(String(cat.id))}
          />
        ))}
      </Box>
    </Box>
  );
}

function FilterRuleRow({ rule, onChange, onRemove, categories, depth }) {
  const ops = OPERATORS_BY_TYPE[rule.type] || [];
  const needsValue = !(rule.type === "category" && rule.op === "empty");
  const isCategory = rule.type === "category" && rule.op !== "empty";
  const isDate = rule.type === "date_start" || rule.type === "date_end";

  const handleTypeChange = (newType) => {
    const newOps = OPERATORS_BY_TYPE[newType] || [];
    const base = { type: newType, op: newOps[0]?.value || "" };
    if (newType === "category") {
      onChange({ ...base, values: [] });
    } else if (newType === "date_start" || newType === "date_end") {
      onChange({ ...base, value: "2010-11-09T00:00" });
    } else {
      onChange({ ...base, value: "" });
    }
  };

  return (
    <RuleRow>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={rule.type || ""}
          label="Type"
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {FILTER_TYPES.map((ft) => (
            <MenuItem key={ft.value} value={ft.value}>
              {ft.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Opérateur</InputLabel>
        <Select
          value={rule.op || ""}
          label="Opérateur"
          onChange={(e) => onChange({ ...rule, op: e.target.value })}
        >
          {ops.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {needsValue && isCategory && (
        <CategoryValueSelect
          values={rule.values}
          onChange={(newValues) => onChange({ ...rule, values: newValues })}
          categories={categories}
        />
      )}

      {needsValue && isDate && (
        <Box
          sx={{
            width: 350,
            flexShrink: 0,
            // react-datepicker renders its own wrapper divs; force them to respect our width
            "& .react-datepicker-wrapper": { width: "100%" },
            "& input": { width: "100%" },
          }}
        >
          <DatePicker
            showIcon
            selected={parseFilterDate(rule.value)}
            onChange={(date) => onChange({ ...rule, value: formatFilterDate(date) })}
            showTimeSelect
            timeCaption="Heure"
            locale="fr"
            dateFormat="yyyy-MM-dd HH:mm"
            timeFormat="HH:mm"
            timeIntervals={20}
            showYearDropdown
            showMonthDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={30}
          />
        </Box>
      )}

      {rule.type === "category" && rule.op === "empty" && (
        // Reserve the same width as the category/date value widgets so the parent layout doesn't resize
        <Box sx={{ width: 350, flexShrink: 0 }} />
      )}

      {needsValue && !isCategory && !isDate && (
        <TextField
          size="small"
          placeholder="Valeur"
          value={rule.value || ""}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          sx={{ width: 350, flexShrink: 0 }}
        />
      )}

      <IconButton size="small" onClick={onRemove} title="Supprimer la règle">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </RuleRow>
  );
}

function FilterGroupContent({ node, onChange, categories, depth }) {
  const conditions = node.conditions || [];

  const updateCondition = (index, newCond) => {
    const next = [...conditions];
    next[index] = newCond;
    onChange({ ...node, conditions: next });
  };

  const removeCondition = (index) => {
    const next = conditions.filter((_, i) => i !== index);
    onChange({ ...node, conditions: next });
  };

  const addRule = () => {
    onChange({
      ...node,
      conditions: [...conditions, { ...DEFAULT_RULE }],
    });
  };

  const addGroup = () => {
    onChange({
      ...node,
      conditions: [
        ...conditions,
        { operator: "AND", conditions: [{ ...DEFAULT_RULE }] },
      ],
    });
  };

  const toggleOperator = () => {
    onChange({
      ...node,
      operator: node.operator === "AND" ? "OR" : "AND",
    });
  };

  return (
    <>
      {conditions.map((cond, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <OperatorToggle onClick={toggleOperator}>
              {node.operator === "AND" ? "Et" : "Ou"}
            </OperatorToggle>
          )}
          {cond.operator ? (
            <FilterGroup
              node={cond}
              onChange={(newGroup) => updateCondition(i, newGroup)}
              onRemove={() => removeCondition(i)}
              categories={categories}
              depth={depth + 1}
            />
          ) : (
            <FilterRuleRow
              rule={cond}
              onChange={(newRule) => updateCondition(i, newRule)}
              onRemove={() => removeCondition(i)}
              categories={categories}
              depth={depth}
            />
          )}
        </React.Fragment>
      ))}

      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <MuiButton size="small" startIcon={<AddIcon />} onClick={addRule}>
          Ajouter une règle
        </MuiButton>
        {depth < 2 && (
          <MuiButton size="small" variant="outlined" startIcon={<AddIcon />} onClick={addGroup}>
            Ajouter un groupe
          </MuiButton>
        )}
      </Box>
    </>
  );
}

function FilterGroup({ node, onChange, onRemove, categories, depth = 0 }) {
  if (depth === 0) {
    return (
      <FilterGroupContent
        node={node}
        onChange={onChange}
        categories={categories}
        depth={depth}
      />
    );
  }

  return (
    <GroupContainer $depth={depth}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        {onRemove && (
          <IconButton size="small" onClick={onRemove} title="Supprimer le groupe">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Groupe de filtres
        </Typography>
      </Box>
      <FilterGroupContent
        node={node}
        onChange={onChange}
        categories={categories}
        depth={depth}
      />
    </GroupContainer>
  );
}

const TimelineFilterBuilder = ({ filters, onChange }) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios
      .get(baseUrl + "/event-categories")
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  const root = filters && filters.operator ? filters : makeDefaultFilters();

  const handleChange = useCallback(
    (newRoot) => {
      onChange(newRoot);
    },
    [onChange]
  );

  return (
    <BuilderContainer>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Filtrage
      </Typography>
      <FilterGroup
        node={root}
        onChange={handleChange}
        categories={categories}
        depth={0}
      />
    </BuilderContainer>
  );
};

export { makeDefaultFilters };
export default TimelineFilterBuilder;

const BuilderContainer = styled.div`
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-2);
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
`;

const GroupContainer = styled.div`
  border: 1px solid var(--border);
  border-left: 3px solid ${(p) => (p.$depth === 0 ? "#1976d2" : "#ff9800")};
  padding: 8px 12px;
  margin: 4px 0;
  /* One step above parent (--surface-2 in BuilderContainer); theme-aware in light/dark */
  background: var(--surface-3);
  border-radius: 4px;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const RuleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  margin: 4px 0;
  max-width: 100%;
`;

const OperatorToggle = styled.button`
  background: var(--accent-bg);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 2px 12px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent);
  align-self: flex-start;
  margin: 2px 0;
  &:hover {
    background: var(--surface-3);
  }
`;

const DropdownList = styled.div`
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--surface);
  max-height: 150px;
  overflow-y: auto;
  width: 100%;
`;

const DropdownItem = styled.div`
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.85rem;
  &:hover {
    background: var(--accent-bg);
  }
`;
